import { useState } from 'react';
import { apiFetch, useApi } from '../hooks/useApi.js';
import { uiAlert } from '../utils/dialogs.js';

// ── Shared UI primitives ──────────────────────────────────────────────────────
function Btn({ children, variant = 'primary', size = 'md', ...props }) {
  const base = {
    borderRadius: 'var(--radius)', fontWeight: 600, cursor: 'pointer', border: 'none',
    fontSize: size === 'sm' ? 12 : 14,
    padding: size === 'sm' ? '5px 12px' : '9px 18px',
    opacity: props.disabled ? 0.5 : 1,
    transition: 'opacity .15s',
  };
  const v = {
    primary: { background: 'var(--pld-green)', color: '#fff' },
    ghost:   { background: 'var(--surface-2)', color: 'var(--text)', border: '1px solid var(--border)' },
    danger:  { background: '#dc2626', color: '#fff' },
    warning: { background: '#d97706', color: '#fff' },
  };
  return <button {...props} style={{ ...base, ...v[variant], ...props.style }}>{children}</button>;
}

function Modal({ title, onClose, width = 520, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} onClick={onClose}>
      <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', width: '100%', maxWidth: width, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid var(--border)' }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: 'var(--text-muted)', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: 24 }}>{children}</div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{label}</label>
      {children}
      {hint && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{hint}</span>}
    </div>
  );
}

function Input(props) {
  return <input {...props} style={{ padding: '8px 11px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', color: 'var(--text)', ...props.style }} />;
}

function SelectEl({ children, ...props }) {
  return <select {...props} style={{ padding: '8px 11px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', color: 'var(--text)', ...props.style }}>{children}</select>;
}

function SectionHeader({ label, action }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
      <h4 style={{ margin: 0, fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted)' }}>{label}</h4>
      {action}
    </div>
  );
}

function InfoBox({ type = 'info', children }) {
  const c = {
    info:    { bg: '#eff6ff', border: '#bfdbfe', text: '#1e40af' },
    warning: { bg: '#fffbeb', border: '#fde68a', text: '#92400e' },
    success: { bg: '#f0fdf4', border: '#bbf7d0', text: '#166534' },
    error:   { bg: '#fef2f2', border: '#fecaca', text: '#991b1b' },
  }[type];
  return (
    <div style={{ padding: '10px 14px', borderRadius: 'var(--radius)', background: c.bg, border: `1px solid ${c.border}`, color: c.text, fontSize: 13 }}>
      {children}
    </div>
  );
}

// ── Constants ─────────────────────────────────────────────────────────────────
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const W1_COLOURS   = { open: { bg: '#fef9c3', text: '#854d0e' }, locked: { bg: '#dbeafe', text: '#1e40af' }, submitted: { bg: '#ede9fe', text: '#5b21b6' }, acknowledged: { bg: '#dcfce7', text: '#166534' } };
const PAY_COLOURS  = { pending: { bg: '#fef9c3', text: '#854d0e' }, invoiced: { bg: '#dbeafe', text: '#1e40af' }, paid: { bg: '#dcfce7', text: '#166534' }, cancelled: { bg: '#f3f4f6', text: '#6b7280' } };
const APPA_COLOURS = { open: { bg: '#fef9c3', text: '#854d0e' }, locked: { bg: '#dbeafe', text: '#1e40af' }, submitted: { bg: '#dcfce7', text: '#166534' } };

const Q_RECEIPTS = [
  { key: 'q19', label: 'Q19 — Opening Stock' },
  { key: 'q20', label: 'Q20 — Stocks held for others' },
  { key: 'q22', label: 'Q22 — Received from EU' },
  { key: 'q23', label: 'Q23 — Received from outside EU' },
  { key: 'q24', label: 'Q24 — Received from UK' },
  { key: 'q25', label: 'Q25 — Other receipts' },
  { key: 'q26', label: 'Q26 — Gains in storage' },
  { key: 'q27', label: 'Q27 — Spirit produced (UK)' },
];
const Q_REMOVALS = [
  { key: 'q28', label: 'Q28 — Duty releases' },
  { key: 'q29', label: 'Q29 — Exports' },
  { key: 'q30', label: 'Q30 — Transfers out (UK)' },
  { key: 'q31', label: 'Q31 — Transfers to EU' },
  { key: 'q32', label: 'Q32 — Duty free removals' },
  { key: 'q33', label: 'Q33 — Losses in storage' },
  { key: 'q34', label: 'Q34 — Production losses' },
  { key: 'q35', label: 'Q35 — Destructions' },
];

const MOV_TYPE_LABELS = { DUTY_RELEASE: 'Duty Release', OWN_BRAND_DISPATCH: 'Own Brand Dispatch', EXPORT: 'Export', DUTY_FREE: 'Duty Free' };

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt     = (n, dp = 4) => { const v = parseFloat(n); return isNaN(v) ? '—' : v.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: dp }); };
const fmtMoney = n => n == null ? '—' : `£${parseFloat(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate  = s => !s ? '—' : new Date(s).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
const periodLabel = p => `${MONTHS[(p.period_month ?? p.month) - 1]} ${p.period_year ?? p.year}`;

// Downloads a report PDF for a W1 or APPA period. Mirrors the blob-download
// pattern used in ProductSpecs.jsx. These PDFs are figures-only reports for
// manual re-keying into HMRC's own services — see PLD-ROADMAP.md.
async function downloadHmrcPdf(kind, id, filename) {
  const path = kind === 'w1-periods' ? `w1-periods/${id}/pdf` : `appa/${id}/pdf`;
  const token = localStorage.getItem('pld_token');
  const res = await fetch(`/api/hmrc/${path}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) { alert('PDF generation failed'); return; }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function StatusBadge({ status, colours, label }) {
  const c = colours[status] ?? { bg: '#f3f4f6', text: '#6b7280' };
  return <span style={{ display: 'inline-block', padding: '3px 9px', borderRadius: 99, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', background: c.bg, color: c.text }}>{label || status?.replace(/_/g, ' ')}</span>;
}

// Own-brand duty is owed to HMRC until the month's duty is paid (Joe, 3 Nov
// 2026); a client's duty status is about re-charging the client.
const dutyStatusLabel = (m) => (m.business_context === 'OWN_BRAND'
  ? ({ pending: 'Owed to HMRC', paid: 'Paid to HMRC' }[m.payment_status] || null) : null);

function TabBar({ tabs, active, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 2, borderBottom: '2px solid var(--border)', marginBottom: 24 }}>
      {tabs.map(t => (
        <button key={t} onClick={() => onChange(t)} style={{ padding: '8px 18px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600, color: active === t ? 'var(--pld-green)' : 'var(--text-muted)', borderBottom: active === t ? '2px solid var(--pld-green)' : '2px solid transparent', marginBottom: -2 }}>
          {t}
        </button>
      ))}
    </div>
  );
}

// ── W1 Periods ────────────────────────────────────────────────────────────────

function OpenPeriodModal({ onClose, onCreated }) {
  const now = new Date();
  const [form, setForm] = useState({ month: now.getMonth() + 1, year: now.getFullYear() });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function handleSubmit(e) {
    e.preventDefault(); setSaving(true); setError('');
    try {
      await apiFetch('/hmrc/w1-periods', { method: 'POST', body: { month: parseInt(form.month), year: parseInt(form.year) } });
      onCreated();
    } catch (err) { setError(err.message); setSaving(false); }
  }

  return (
    <Modal title="Open W1 Period" onClose={onClose} width={400}>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <InfoBox type="info">Opening a period automatically populates Q19 from the previous period's Q36 closing stock.</InfoBox>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Month">
            <SelectEl value={form.month} onChange={e => set('month', e.target.value)}>
              {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
            </SelectEl>
          </Field>
          <Field label="Year">
            <Input type="number" min="2020" max="2099" value={form.year} onChange={e => set('year', e.target.value)} />
          </Field>
        </div>
        {error && <p style={{ margin: 0, color: '#dc2626', fontSize: 13 }}>{error}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Btn type="button" variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving}>{saving ? 'Opening…' : 'Open Period'}</Btn>
        </div>
      </form>
    </Modal>
  );
}

function LockPeriodModal({ period, onClose, onLocked }) {
  const [totp, setTotp] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e) {
    e.preventDefault(); setSaving(true); setError('');
    try {
      await apiFetch(`/hmrc/w1-periods/${period.id}/lock`, { method: 'POST', body: {}, headers: { 'X-TOTP-Code': totp } });
      onLocked();
    } catch (err) { setError(err.message); setSaving(false); }
  }

  return (
    <Modal title="Lock W1 Period" onClose={onClose} width={440}>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <InfoBox type="warning">Locking <strong>{periodLabel(period)}</strong> is irreversible. The period cannot be modified after locking.</InfoBox>
        <Field label="Authenticator Code (2FA)" hint="6-digit code from your authenticator app.">
          <Input type="text" inputMode="numeric" maxLength={6} value={totp} onChange={e => setTotp(e.target.value.replace(/\D/g, ''))} placeholder="000000" autoFocus style={{ letterSpacing: '0.25em', fontFamily: 'monospace', fontSize: 18, textAlign: 'center' }} />
        </Field>
        {error && <p style={{ margin: 0, color: '#dc2626', fontSize: 13 }}>{error}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Btn type="button" variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" variant="warning" disabled={saving || totp.length !== 6}>{saving ? 'Locking…' : 'Lock Period'}</Btn>
        </div>
      </form>
    </Modal>
  );
}

function AcknowledgeModal({ period, onClose, onAcknowledged }) {
  const [ref, setRef] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e) {
    e.preventDefault(); setSaving(true); setError('');
    try {
      await apiFetch(`/hmrc/w1-periods/${period.id}/acknowledge`, { method: 'POST', body: { acknowledgement_ref: ref.trim() } });
      onAcknowledged();
    } catch (err) { setError(err.message); setSaving(false); }
  }

  return (
    <Modal title="Record HMRC Acknowledgement" onClose={onClose} width={440}>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Field label="Acknowledgement Reference" hint="The reference number provided by HMRC on submission.">
          <Input value={ref} onChange={e => setRef(e.target.value)} placeholder="e.g. ACK-2025-001234" autoFocus required />
        </Field>
        {error && <p style={{ margin: 0, color: '#dc2626', fontSize: 13 }}>{error}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Btn type="button" variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving || !ref.trim()}>{saving ? 'Saving…' : 'Record Acknowledgement'}</Btn>
        </div>
      </form>
    </Modal>
  );
}

function W1ReturnLines({ lines }) {
  if (!lines?.length) return <p style={{ margin: 0, fontSize: 14, color: 'var(--text-muted)' }}>No return lines found — recalculate to populate.</p>;
  const line = lines[0];

  const qRow = ({ key, label }) => {
    const val = parseFloat(line[`${key}_litres`] || 0);
    const hasVal = val !== 0;
    return (
      <tr key={key} style={{ borderBottom: '1px solid var(--border)' }}>
        <td style={{ padding: '7px 12px', fontSize: 13, color: hasVal ? 'var(--text)' : 'var(--text-muted)' }}>{label}</td>
        <td style={{ padding: '7px 12px', textAlign: 'right', fontFamily: 'monospace', fontSize: 13, fontWeight: hasVal ? 600 : 400, color: hasVal ? 'var(--text)' : 'var(--text-muted)' }}>{hasVal ? `${fmt(val)} LPA` : '—'}</td>
      </tr>
    );
  };

  const q36 = parseFloat(line.q36_litres || 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <InfoBox type={line.is_balanced ? 'success' : 'warning'}>
        {line.is_balanced
          ? '✓ Balance check passed — Q19–Q35 reconciles to Q36 closing stock.'
          : `⚠ Balance check failed. Variance: ${fmt(parseFloat(line.balance_variance_litres || 0))} LPA. Recalculate or apply a manual adjustment before locking.`}
      </InfoBox>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div>
          <h5 style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted)' }}>Receipts</h5>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}><tbody>{Q_RECEIPTS.map(qRow)}</tbody></table>
        </div>
        <div>
          <h5 style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted)' }}>Removals</h5>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}><tbody>{Q_REMOVALS.map(qRow)}</tbody></table>
        </div>
      </div>

      <div style={{ background: q36 >= 0 ? '#f0fdf4' : '#fef2f2', border: `1px solid ${q36 >= 0 ? '#bbf7d0' : '#fecaca'}`, borderRadius: 'var(--radius)', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontWeight: 700 }}>Q36 — Closing Stock</span>
        <span style={{ fontFamily: 'monospace', fontSize: 16, fontWeight: 700, color: q36 >= 0 ? '#166534' : '#991b1b' }}>{fmt(q36)} LPA</span>
      </div>

      {line.manually_adjusted && (
        <InfoBox type="warning">Manual adjustment applied. Notes: {line.manual_adjustment_notes || '(none)'}</InfoBox>
      )}
    </div>
  );
}

function W1PeriodDetail({ periodId, onBack }) {
  const { data, loading, error, refetch } = useApi(`/hmrc/w1-periods/${periodId}`, [periodId]);
  const [busy, setBusy] = useState('');
  const [apiError, setApiError] = useState('');
  const [showLock, setShowLock] = useState(false);
  const [showAck, setShowAck] = useState(false);

  async function doAction(path, body = {}) {
    setBusy(path); setApiError('');
    try { await apiFetch(`/hmrc/w1-periods/${periodId}/${path}`, { method: 'POST', body }); refetch(); }
    catch (err) { setApiError(err.message); }
    setBusy('');
  }

  if (loading) return <div style={{ padding: 32, color: 'var(--text-muted)' }}>Loading…</div>;
  if (error || !data) return <div style={{ padding: 32, color: '#dc2626' }}>Failed to load period.</div>;

  const { lines, ...period } = data;
  const status = period.status;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {showLock && <LockPeriodModal period={period} onClose={() => setShowLock(false)} onLocked={() => { setShowLock(false); refetch(); }} />}
      {showAck  && <AcknowledgeModal period={period} onClose={() => setShowAck(false)} onAcknowledged={() => { setShowAck(false); refetch(); }} />}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 20, lineHeight: 1, padding: 0 }}>←</button>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <StatusBadge status={status} colours={W1_COLOURS} />
            <span style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'monospace' }}>{period.warehouse_id}</span>
          </div>
          <h2 style={{ margin: '4px 0 0', fontSize: 20, fontWeight: 700 }}>W1 — {periodLabel(period)}</h2>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Btn variant="ghost" size="sm" onClick={() => downloadHmrcPdf('w1-periods', periodId, `W1-${periodLabel(period)}.pdf`)}>↓ PDF</Btn>
          {status === 'open' && (
            <>
              <Btn variant="ghost" size="sm" disabled={!!busy} onClick={() => doAction('recalculate')}>{busy === 'recalculate' ? 'Recalculating…' : 'Recalculate'}</Btn>
              <Btn variant="warning" size="sm" onClick={() => setShowLock(true)}>Lock Period</Btn>
            </>
          )}
          {status === 'locked'    && <Btn size="sm" disabled={!!busy} onClick={() => doAction('submit')}>{busy === 'submit' ? 'Submitting…' : 'Mark as Submitted'}</Btn>}
          {status === 'submitted' && <Btn size="sm" onClick={() => setShowAck(true)}>Record Acknowledgement</Btn>}
        </div>
      </div>

      {/* Meta cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
        {[
          { label: 'Period',       value: periodLabel(period) },
          { label: 'Warehouse',    value: period.warehouse_id },
          { label: 'Submitted',    value: fmtDate(period.submitted_at) },
          { label: 'Acknowledged', value: fmtDate(period.acknowledged_at) },
          { label: 'Ack. Ref',     value: period.acknowledgement_ref ?? '—' },
        ].map(({ label, value }) => (
          <div key={label} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '10px 14px' }}>
            <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)', marginBottom: 3 }}>{label}</div>
            <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{value}</div>
          </div>
        ))}
      </div>

      {apiError && <InfoBox type="error">{apiError}</InfoBox>}

      {/* Return lines */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 20 }}>
        <SectionHeader label="Return Lines — Other Spirits" />
        <W1ReturnLines lines={lines} />
      </div>
    </div>
  );
}

function W1PeriodsTab() {
  const { data, loading, error, refetch } = useApi('/hmrc/w1-periods');
  const [showOpen, setShowOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const periods = Array.isArray(data) ? data : [];

  if (selectedId) return <W1PeriodDetail periodId={selectedId} onBack={() => { setSelectedId(null); refetch(); }} />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {showOpen && <OpenPeriodModal onClose={() => setShowOpen(false)} onCreated={() => { setShowOpen(false); refetch(); }} />}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Each month's period opens automatically and its figures update as stock moves. Lock and submit when you're ready.</span>
      </div>
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
        ) : error ? (
          <div style={{ padding: 32, textAlign: 'center', color: '#dc2626' }}>Failed to load periods.</div>
        ) : !periods.length ? (
          <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)' }}>No W1 periods yet — open one to get started.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border)', background: 'var(--surface-2)' }}>
                {['Period', 'Warehouse', 'Status', 'Submitted', 'Acknowledged', ''].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {periods.map(p => (
                <tr key={p.id} onClick={() => setSelectedId(p.id)} style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}>
                  <td style={{ padding: '10px 14px', fontWeight: 600 }}>{periodLabel(p)}</td>
                  <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontSize: 12, color: 'var(--text-muted)' }}>{p.warehouse_id}</td>
                  <td style={{ padding: '10px 14px' }}><StatusBadge status={p.status} colours={W1_COLOURS} /></td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-muted)' }}>{fmtDate(p.submitted_at)}</td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-muted)' }}>{fmtDate(p.acknowledged_at)}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', color: 'var(--text-muted)', fontSize: 18 }}>›</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ── Duty Movements ────────────────────────────────────────────────────────────

function DutyMovementsTab() {
  const [filterStatus, setFilterStatus] = useState('');
  const { data, loading, error, refetch } = useApi(`/hmrc/duty-movements${filterStatus ? `?payment_status=${filterStatus}` : ''}`, [filterStatus]);
  const movements = Array.isArray(data) ? data : [];
  const [sending, setSending] = useState(null);
  // A contract client's duty release gets a draft invoice in Xero (Joe, 27 Oct 2026).
  const invoiceable = (m) => m.movement_type === 'DUTY_RELEASE' && m.client_id && !m.fulfilment_order_id && m.payment_status !== 'cancelled';
  async function sendToXero(m) {
    setSending(m.id);
    try { await apiFetch(`/xero/duty-movements/${m.id}`, { method: 'POST' }); } catch (e) { await uiAlert(e.message); } finally { setSending(null); refetch(); }
  }

  const STATUSES = [['', 'All'], ['pending', 'Pending'], ['invoiced', 'Invoiced'], ['paid', 'Paid'], ['cancelled', 'Cancelled']];
  const pill = (s) => ({ padding: '5px 12px', borderRadius: 99, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none', background: filterStatus === s ? 'var(--pld-green)' : 'var(--surface-2)', color: filterStatus === s ? '#fff' : 'var(--text)' });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {STATUSES.map(([s, l]) => <button key={s} style={pill(s)} onClick={() => setFilterStatus(s)}>{l}</button>)}
      </div>
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
        ) : error ? (
          <div style={{ padding: 32, textAlign: 'center', color: '#dc2626' }}>Failed to load duty movements.</div>
        ) : !movements.length ? (
          <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)' }}>No duty movements found.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)', background: 'var(--surface-2)' }}>
                  {['Ref', 'Type', 'Context', 'LPA', 'Duty', 'Status', 'Date', 'Xero'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: h === 'LPA' || h === 'Duty' ? 'right' : 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {movements.map(m => (
                  <tr key={m.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>{m.duty_ref}</td>
                    <td style={{ padding: '10px 14px' }}>{MOV_TYPE_LABELS[m.movement_type] ?? m.movement_type}</td>
                    <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--text-muted)' }}>{m.business_context}</td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: 'monospace', fontSize: 13 }}>{fmt(m.lpa)} LPA</td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: 'monospace', fontSize: 13, fontWeight: 600 }}>{fmtMoney(m.duty_amount)}</td>
                    <td style={{ padding: '10px 14px' }}><StatusBadge status={m.payment_status} colours={PAY_COLOURS} label={dutyStatusLabel(m)} /></td>
                    <td style={{ padding: '10px 14px', color: 'var(--text-muted)', whiteSpace: 'nowrap', fontSize: 13 }}>{fmtDate(m.created_at)}</td>
                    <td style={{ padding: '10px 14px', fontSize: 12 }}>
                      {m.xero_invoice_id ? <span style={{ color: '#166534' }}>✓ In Xero</span>
                        : invoiceable(m) ? (<>
                          {m.xero_error && <div style={{ color: '#991b1b', marginBottom: 4, maxWidth: 220 }}>{m.xero_error}</div>}
                          <button onClick={() => sendToXero(m)} disabled={sending === m.id} style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--surface-2)', cursor: 'pointer' }}>{sending === m.id ? 'Sending…' : 'Send to Xero'}</button>
                        </>)
                        : <span style={{ color: 'var(--text-muted)' }}>{m.fulfilment_order_id ? 'On fulfilment bill' : '—'}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && movements.length > 0 && (
          <div style={{ padding: '8px 14px', borderTop: '1px solid var(--border)', fontSize: 12, color: 'var(--text-muted)', background: 'var(--surface-2)' }}>
            {movements.length} movement{movements.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>
    </div>
  );
}

// ── APPA ──────────────────────────────────────────────────────────────────────

function CalculateAppaModal({ onClose, onCalculated }) {
  const now = new Date();
  const [form, setForm] = useState({ month: now.getMonth() + 1, year: now.getFullYear() });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function handleSubmit(e) {
    e.preventDefault(); setSaving(true); setError('');
    try {
      await apiFetch('/hmrc/appa/calculate', { method: 'POST', body: { month: parseInt(form.month), year: parseInt(form.year) } });
      onCalculated();
    } catch (err) { setError(err.message); setSaving(false); }
  }

  return (
    <Modal title="Calculate APPA Period" onClose={onClose} width={400}>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <InfoBox type="info">Aggregates all duty movements for the selected month into the APPA return. Safe to re-run before locking.</InfoBox>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Month">
            <SelectEl value={form.month} onChange={e => set('month', e.target.value)}>
              {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
            </SelectEl>
          </Field>
          <Field label="Year">
            <Input type="number" min="2020" max="2099" value={form.year} onChange={e => set('year', e.target.value)} />
          </Field>
        </div>
        {error && <p style={{ margin: 0, color: '#dc2626', fontSize: 13 }}>{error}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Btn type="button" variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving}>{saving ? 'Calculating…' : 'Calculate'}</Btn>
        </div>
      </form>
    </Modal>
  );
}

function AppaTab() {
  const { data, loading, error, refetch } = useApi('/hmrc/appa');
  async function markPaid(p, paid) {
    try {
      const r = await apiFetch(`/hmrc/appa/${p.id}/duty-paid`, { method: 'POST', body: { paid } });
      const n = (r?.data ?? r)?.movements_updated ?? 0;
      refetch();
      await uiAlert(paid ? `Marked paid — ${n} own-brand duty movement${n === 1 ? '' : 's'} now show as paid to HMRC.` : `Undone — ${n} back to owed.`);
    } catch (e) { await uiAlert(e.message); }
  }
  const [showCalc, setShowCalc] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const periods = Array.isArray(data) ? data : [];
  const selected = periods.find(p => p.id === selectedId);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {showCalc && <CalculateAppaModal onClose={() => setShowCalc(false)} onCalculated={() => { setShowCalc(false); refetch(); }} />}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Btn onClick={() => setShowCalc(true)}>+ Calculate Period</Btn>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: selectedId ? '1fr 1fr' : '1fr', gap: 16 }}>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
          ) : error ? (
            <div style={{ padding: 32, textAlign: 'center', color: '#dc2626' }}>Failed to load APPA periods.</div>
          ) : !periods.length ? (
            <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)' }}>No APPA periods yet.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)', background: 'var(--surface-2)' }}>
                  {['Period', 'Total LPA', 'Total Duty', 'Status', ''].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: h === 'Total LPA' || h === 'Total Duty' ? 'right' : 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {periods.map(p => (
                  <tr key={p.id} onClick={() => setSelectedId(p.id === selectedId ? null : p.id)}
                    style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer', background: p.id === selectedId ? 'var(--surface-2)' : '' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
                    onMouseLeave={e => e.currentTarget.style.background = p.id === selectedId ? 'var(--surface-2)' : ''}>
                    <td style={{ padding: '10px 14px', fontWeight: 600 }}>{periodLabel(p)}</td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: 'monospace', fontSize: 13 }}>{fmt(p.total_lpa)} LPA</td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: 'monospace', fontSize: 13, fontWeight: 600 }}>{fmtMoney(p.total_duty_amount)}</td>
                    <td style={{ padding: '10px 14px' }}><StatusBadge status={p.status} colours={APPA_COLOURS} /></td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', color: 'var(--text-muted)', fontSize: 18 }}>›</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {selected && (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <SectionHeader label={periodLabel(selected)} action={
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <Btn variant="ghost" size="sm" onClick={() => downloadHmrcPdf('appa', selected.id, `APPA-${periodLabel(selected)}.pdf`)}>↓ PDF</Btn>
                <StatusBadge status={selected.status} colours={APPA_COLOURS} />
              </div>
            } />
            {[
              { label: 'Own Brand LPA',    value: `${fmt(selected.own_brand_lpa)} LPA` },
              { label: 'Own Brand Duty',   value: fmtMoney(selected.own_brand_duty_amount) },
              { label: 'Contract LPA',     value: `${fmt(selected.contract_lpa)} LPA` },
              { label: 'Contract Duty',    value: fmtMoney(selected.contract_duty_amount) },
            ].map(({ label, value }) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                <span style={{ fontSize: 14, color: 'var(--text-muted)' }}>{label}</span>
                <span style={{ fontSize: 14, fontWeight: 600, fontFamily: 'monospace' }}>{value}</span>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0' }}>
              <span style={{ fontSize: 15, fontWeight: 700 }}>Total Duty</span>
              <span style={{ fontSize: 15, fontWeight: 700, fontFamily: 'monospace', color: 'var(--pld-green)' }}>{fmtMoney(selected.total_duty_amount)}</span>
            </div>
            {selected.calculated_at && (
              <p style={{ margin: 0, fontSize: 12, color: 'var(--text-muted)' }}>Calculated: {fmtDate(selected.calculated_at)}</p>
            )}
            {/* The month's duty paid to HMRC: its own-brand duty turns from owed to paid. */}
            {selected.calculated_at && (
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', fontSize: 13 }}>
                {selected.duty_paid_at
                  ? <><span style={{ color: '#166534', fontWeight: 600 }}>Duty paid to HMRC {fmtDate(selected.duty_paid_at)}</span>
                      <Btn variant="ghost" size="sm" onClick={() => markPaid(selected, false)}>Undo</Btn></>
                  : <Btn size="sm" onClick={() => markPaid(selected, true)}>Mark duty paid to HMRC</Btn>}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Duty Rates ────────────────────────────────────────────────────────────────

const RATE_TYPES = [
  { value: 'other_spirits', label: 'Spirits 22% ABV and over' },
  { value: 'spirits_8_5_to_22', label: 'Spirits / spirit drinks 8.5% to under 22% ABV' },
];

function DutyRatesTab() {
  const { data, loading, error, refetch } = useApi('/hmrc/duty-rates');
  const rates = Array.isArray(data) ? data : (data?.data ?? []);
  const [form, setForm] = useState({ spirit_type: 'other_spirits', rate_per_lpa: '', effective_from: '' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  async function add(e) {
    e.preventDefault(); setSaving(true); setMsg('');
    try {
      await apiFetch('/hmrc/duty-rates', { method: 'POST', body: { ...form, rate_per_lpa: parseFloat(form.rate_per_lpa) } });
      setForm(f => ({ ...f, rate_per_lpa: '' })); setMsg('Saved — the previous rate for this type is now historical.'); refetch?.();
    } catch (err) { setMsg(err.message); } finally { setSaving(false); }
  }

  return (
    <div>
    <form onSubmit={add} style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 14, marginBottom: 14 }}>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600 }}>Rate for
        <select value={form.spirit_type} onChange={e => setForm(f => ({ ...f, spirit_type: e.target.value }))} style={{ padding: '7px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text)' }}>
          {RATE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600 }}>£ per litre of pure alcohol
        <input required type="number" step="0.01" min="0.01" value={form.rate_per_lpa} onChange={e => setForm(f => ({ ...f, rate_per_lpa: e.target.value }))} placeholder="e.g. 33.99" style={{ padding: '7px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text)', width: 130 }} />
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600 }}>From
        <input required type="date" value={form.effective_from} onChange={e => setForm(f => ({ ...f, effective_from: e.target.value }))} style={{ padding: '7px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text)' }} />
      </label>
      <Btn type="submit" disabled={saving}>{saving ? 'Saving…' : 'Add rate'}</Btn>
      {msg && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{msg}</span>}
    </form>
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
      {loading ? (
        <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
      ) : error ? (
        <div style={{ padding: 32, textAlign: 'center', color: '#dc2626' }}>Failed to load duty rates.</div>
      ) : !rates.length ? (
        <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)' }}>No duty rates yet — add the current HMRC rates above.</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--border)', background: 'var(--surface-2)' }}>
              {['Spirit Type', 'Rate per LPA', 'Effective From', 'Effective To', 'Status'].map(h => (
                <th key={h} style={{ padding: '10px 14px', textAlign: h === 'Rate per LPA' ? 'right' : 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rates.map(r => (
              <tr key={r.id} style={{ borderBottom: '1px solid var(--border)', opacity: r.is_current ? 1 : 0.6 }}>
                <td style={{ padding: '10px 14px', fontWeight: r.is_current ? 600 : 400 }}>{RATE_TYPES.find(t => t.value === r.spirit_type)?.label || r.spirit_type?.replace(/_/g, ' ')}</td>
                <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 700, color: r.is_current ? 'var(--pld-green)' : 'var(--text-muted)' }}>{fmtMoney(r.rate_per_lpa)}/LPA</td>
                <td style={{ padding: '10px 14px', color: 'var(--text-muted)' }}>{fmtDate(r.effective_from)}</td>
                <td style={{ padding: '10px 14px', color: 'var(--text-muted)' }}>{r.effective_to ? fmtDate(r.effective_to) : '—'}</td>
                <td style={{ padding: '10px 14px' }}>
                  {r.is_current
                    ? <span style={{ fontSize: 11, fontWeight: 700, color: '#166534', background: '#dcfce7', padding: '2px 8px', borderRadius: 99 }}>Current</span>
                    : <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Historical</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
    </div>
  );
}

// ── Default export ────────────────────────────────────────────────────────────
export default function Hmrc() {
  const [tab, setTab] = useState('W1 Periods');
  return (
    <div>
      <TabBar tabs={['W1 Periods', 'Duty Movements', 'APPA', 'Duty Rates']} active={tab} onChange={setTab} />
      {tab === 'W1 Periods'     && <W1PeriodsTab />}
      {tab === 'Duty Movements' && <DutyMovementsTab />}
      {tab === 'APPA'           && <AppaTab />}
      {tab === 'Duty Rates'     && <DutyRatesTab />}
    </div>
  );
}
